import { SearchRequest, SegmentType } from '../Library/LibrarySearch';
import { LibraryManagerMultiLibrarySearch } from '../LibraryManager/LibraryManagerMultiLibrarySearch';
import { BaseSelection } from './BaseSelection';

export type MomentIdentifier = {
  libraryId: string;
  mediaId: string;
  startTime: number;
  endTime: number;
};

export const encodeMomentToString = (
  libraryId: string,
  mediaId: string,
  startTime: number,
  endTime: number,
): MomentIdentifierString => {
  return `${libraryId}:${mediaId}:${startTime}:${endTime}` as MomentIdentifierString;
};

export const decodeMomentFromString = (momentString: MomentIdentifierString): MomentIdentifier => {
  const [libraryId, mediaId, startTime, endTime] = momentString.split(':');

  if (!libraryId || !mediaId || !startTime || !endTime) {
    throw new Error('Invalid moment string');
  }

  if (isNaN(parseFloat(startTime)) || isNaN(parseFloat(endTime))) {
    throw new Error('Invalid start or end time');
  }

  return {
    libraryId,
    mediaId,
    startTime: parseFloat(startTime),
    endTime: parseFloat(endTime),
  } as MomentIdentifier;
};

const encodeMomentIndentifierToString = (
  momentIdentifier: MomentIdentifier,
): MomentIdentifierString => {
  return encodeMomentToString(
    momentIdentifier.libraryId,
    momentIdentifier.mediaId,
    momentIdentifier.startTime,
    momentIdentifier.endTime,
  );
};

export type SearchContextOnSelect = {
  searchRequest: SearchRequest | null;
  resultType: SegmentType;
};

// Branded type for validated moment identifier strings
// Format: libraryId:mediaId:searchType:startTime:endTime
export type MomentIdentifierString = string & { __brand: 'MomentIdentifierString' };

export type MomentSearchResultItem = {
  momentId: MomentIdentifierString;
  searchRequest: SearchRequest | null;
};

export class MomentSelection extends BaseSelection<MomentSearchResultItem> {
  private readonly search: LibraryManagerMultiLibrarySearch;

  momentDetails: Map<MomentIdentifier, MomentSearchResultItem> = new Map();

  searchContextOnSelect: Map<MomentIdentifierString, SearchContextOnSelect> = new Map();

  // Legacy property for backward compatibility
  get selectedMoments() {
    return this.selectedItems;
  }

  // Legacy method for backward compatibility
  toggleMomentSelection(momentIdentifier: MomentIdentifier | MomentIdentifierString) {
    this.setIsSelecting(true);
    const momentIdentifierString =
      typeof momentIdentifier === 'string'
        ? momentIdentifier
        : encodeMomentIndentifierToString(momentIdentifier);
    if (this.selectedItems.has(momentIdentifierString)) {
      this.selectedItems.delete(momentIdentifierString);
      this.searchContextOnSelect.delete(momentIdentifierString);
    } else {
      this.selectedItems.add(momentIdentifierString);
      this.searchContextOnSelect.set(momentIdentifierString, {
        searchRequest: this.search.request,
        resultType: this.search.resultType,
      });
    }
    return momentIdentifierString;
  }

  /* We need toggleMomentSelection because we need to add searchContextOnSelect, and it uses a different set of arguments. */
  override toggleSelection(itemId: string) {
    throw new Error(
      'toggleSelection is not supported for MomentSelection, use toggleMomentSelection instead',
    );
  }

  // Select all affects all moments in the result list.
  override selectAll() {
    const items = this.getAvailableItems();
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const momentId = items[i].momentId;
        if (!this.selectedItems.has(momentId)) {
          this.toggleMomentSelection(momentId);
        }
      }
    }
    this.setIsSelecting(true);
  }

  // Deselect all only affects those that are currently visibile in the result list.
  override deselectAll() {
    const items = this.getAvailableItems();
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const momentId = items[i].momentId;
        if (this.selectedItems.has(momentId)) {
          this.toggleMomentSelection(momentId);
        }
      }
    }
  }

  override stopSelecting() {
    this.selectedItems.clear();
    this.searchContextOnSelect.clear();
    this.setIsSelecting(false);
  }

  constructor(search: LibraryManagerMultiLibrarySearch) {
    super();
    this.search = search;
  }

  protected getAvailableItems(): MomentSearchResultItem[] {
    return (
      this.search.results?.results.map((result) => ({
        momentId: encodeMomentIndentifierToString({
          libraryId: result.libraryId,
          mediaId: result.mediaId,
          startTime: result.startTime,
          endTime: result.endTime,
        }),
        searchRequest: this.search.request,
      })) ?? []
    );
  }

  protected getItemId(item: MomentSearchResultItem): string {
    return item.momentId;
  }
}
